import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Button, Empty, Image, Input, Spin, Tooltip, Typography, message } from 'antd'
import { CheckCircleOutlined, CheckOutlined, CloseOutlined, DownloadOutlined, FileOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { chatsApi } from '../../services/api'
import { chatWorkspaceApi, type PlainChatMessage } from '../../services/chatWorkspaceApi'
import { useLeadChatEventsStore } from '../../store/leadChatEventsStore'
import FormattedText from '../FormattedText'

const { Text } = Typography
const PAGE = 100

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    return typeof detail === 'string' ? detail : 'Не удалось выполнить действие'
}

function formatSize(size?: number | null): string {
    if (!size) return ''
    if (size < 1024) return `${size} Б`
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} КБ`
    return `${(size / 1024 / 1024).toFixed(1)} МБ`
}

function Media({ item }: { item: PlainChatMessage }) {
    const url = item.media_file_path ? chatWorkspaceApi.plainMediaUrl(item.media_file_path) : ''
    const type = (item.content_type || '').toLowerCase()
    const mime = (item.media_mime_type || '').toLowerCase()
    const name = item.media_file_name || 'Файл'
    if (!url) return null
    if (type === 'photo' || type === 'sticker' || mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(name)) {
        return (
            <div className="lead-media lead-media-image">
                <Image src={url} alt={name} style={{ maxWidth: 320, maxHeight: 320, objectFit: 'contain' }} />
            </div>
        )
    }
    if (type === 'video' || type === 'animation' || type === 'video_note' || mime.startsWith('video/')) {
        return <div className="lead-media lead-media-video"><video src={url} controls /></div>
    }
    if (type === 'audio' || type === 'voice' || mime.startsWith('audio/')) {
        return <div className="lead-media lead-media-audio"><audio src={url} controls /></div>
    }
    return (
        <a className="lead-media-file" href={url} download={name} target="_blank" rel="noreferrer">
            <FileOutlined />
            <span>
                <strong>{name}</strong>
                <small>{[type || 'файл', formatSize(item.media_size)].filter(Boolean).join(' · ')}</small>
            </span>
            <DownloadOutlined />
        </a>
    )
}

/**
 * Переписка диалога без карточки - в том же окне, что и у лида: новые
 * сообщения приходят сразу, история подгружается при прокрутке вверх, есть
 * медиа, отправка файлов и многострочный ввод (Enter - отправить,
 * Shift+Enter - новая строка).
 */
export default function PlainChatWindow({ chatId, onSent }: { chatId: number; onSent?: () => void }) {
    const [items, setItems] = useState<PlainChatMessage[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingEarlier, setLoadingEarlier] = useState(false)
    const [hasEarlier, setHasEarlier] = useState(true)
    const [text, setText] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [sending, setSending] = useState(false)
    const scrollRef = useRef<HTMLDivElement | null>(null)
    const fileRef = useRef<HTMLInputElement | null>(null)
    const stickToBottom = useRef(true)
    // Прокрутка - после отрисовки сообщений: вниз (открыли диалог, пришло
    // новое) или на прежнее место (подгрузили историю сверху).
    const pendingScroll = useRef<{ bottom?: boolean; keepFrom?: number } | null>(null)
    const ready = useRef(false)
    const preview = useLeadChatEventsStore((state) => state.chatPreviews[chatId])

    const scrollToBottom = () => {
        pendingScroll.current = { bottom: true }
    }

    useLayoutEffect(() => {
        const box = scrollRef.current
        const pending = pendingScroll.current
        if (!box || !pending) return
        if (pending.bottom) box.scrollTop = box.scrollHeight
        else if (pending.keepFrom !== undefined) box.scrollTop = box.scrollHeight - pending.keepFrom
        pendingScroll.current = null
        ready.current = true
    }, [items])

    const merge = (current: PlainChatMessage[], fresh: PlainChatMessage[]) => {
        const byId = new Map(current.map((item) => [item.id, item]))
        fresh.forEach((item) => byId.set(item.id, item))
        return Array.from(byId.values()).sort((a, b) =>
            dayjs(a.date).valueOf() - dayjs(b.date).valueOf() || a.id - b.id)
    }

    const loadLatest = useCallback(async (initial: boolean) => {
        if (initial) setLoading(true)
        try {
            const { data } = await chatWorkspaceApi.getPlainMessages(chatId, { limit: PAGE })
            setItems((current) => (initial ? data : merge(current, data)))
            if (initial) setHasEarlier(data.length >= PAGE)
            if (initial || stickToBottom.current) scrollToBottom()
        } catch (error) {
            if (initial) message.error(extractError(error))
        } finally {
            if (initial) setLoading(false)
        }
    }, [chatId])

    useEffect(() => {
        ready.current = false
        setItems([])
        setText('')
        setFile(null)
        stickToBottom.current = true
        void loadLatest(true)
    }, [chatId, loadLatest])

    // Новое сообщение в этот диалог (событие из общего канала) - дочитываем.
    useEffect(() => {
        if (preview?.bump) void loadLatest(false)
    }, [preview?.bump, loadLatest])

    const loadEarlier = async () => {
        if (loadingEarlier || !hasEarlier || !items.length) return
        const box = scrollRef.current
        const heightBefore = box?.scrollHeight || 0
        setLoadingEarlier(true)
        try {
            const oldest = items[0]
            const { data } = await chatWorkspaceApi.getPlainMessages(chatId, {
                limit: PAGE, before_date: oldest.date || undefined, before_id: oldest.id,
            })
            setHasEarlier(data.length >= PAGE)
            pendingScroll.current = { keepFrom: heightBefore }
            setItems((current) => merge(current, data))
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoadingEarlier(false)
        }
    }

    const onScroll = () => {
        const box = scrollRef.current
        if (!box) return
        stickToBottom.current = box.scrollHeight - box.scrollTop - box.clientHeight < 80
        if (ready.current && box.scrollTop < 60) void loadEarlier()
    }

    const send = async () => {
        const body = text.trim()
        if (sending || (!body && !file)) return
        setSending(true)
        try {
            if (file) {
                const { data } = await chatWorkspaceApi.sendPlainMedia(chatId, file, body)
                scrollToBottom()
                setItems((current) => merge(current, [data]))
            } else {
                await chatsApi.sendMessage(chatId, body)
                await loadLatest(false)
            }
            setText('')
            setFile(null)
            stickToBottom.current = true
            scrollToBottom()
            onSent?.()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSending(false)
        }
    }

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault()
            void send()
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, gap: 10 }}>
            <div
                ref={scrollRef}
                onScroll={onScroll}
                className="lead-chat-messages"
                style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
            >
                <Spin spinning={loading}>
                    {loadingEarlier && <div style={{ textAlign: 'center', padding: 6 }}><Spin size="small" /></div>}
                    {!loading && items.length === 0 ? (
                        <Empty description="Сообщений пока нет" />
                    ) : (
                        items.map((item) => (
                            <div key={item.id} className={`lead-message-row ${item.outgoing ? 'is-outgoing' : 'is-incoming'}`}>
                                <div className="lead-message-bubble">
                                    <Media item={item} />
                                    {item.text && (
                                        item.text_html
                                            ? <FormattedText html={item.text_html} />
                                            : <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.text}</div>
                                    )}
                                    {item.deleted_by_lead && <Text type="secondary" style={{ fontSize: 12 }}>Удалено собеседником</Text>}
                                    <div className="lead-message-meta">
                                        <span>{item.date ? dayjs(item.date).format('DD.MM HH:mm') : ''}</span>
                                        {item.outgoing && item.operator_name && <span>{item.operator_name}</span>}
                                        {item.outgoing && (
                                            <span className={`lead-message-status ${item.is_read ? 'is-read' : ''}`}>
                                                {item.is_read ? <CheckCircleOutlined /> : <CheckOutlined />}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </Spin>
            </div>

            {file && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileOutlined />
                    <Text ellipsis style={{ maxWidth: 360 }}>{file.name}</Text>
                    <Text type="secondary">{formatSize(file.size)}</Text>
                    <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setFile(null)} />
                </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <input
                    ref={fileRef}
                    type="file"
                    hidden
                    onChange={(event) => {
                        setFile(event.target.files?.[0] || null)
                        event.target.value = ''
                    }}
                />
                <Tooltip title="Прикрепить файл">
                    <Button icon={<PaperClipOutlined />} onClick={() => fileRef.current?.click()} />
                </Tooltip>
                <Input.TextArea
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={onKeyDown}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    placeholder={file ? 'Подпись к файлу…' : 'Сообщение… (Shift+Enter — новая строка)'}
                />
                <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={() => void send()}>
                    Отправить
                </Button>
            </div>
        </div>
    )
}
